/**
 * attentionMetrics.js — lightweight attention-flow instrumentation.
 *
 * Metrics captured per the PART 6 targets:
 *   • taps_to_first_activity  — clicks from a home/plan page until the
 *     first activity link is followed.  Target: ≤ 1 from home.
 *   • gap_between_activities  — seconds between one activity end and
 *     the next activity start.  Target: median < 10 s.
 *   • mid_activity_abandonment — activity started but never marked done.
 *   • session_completion_rate — % of daily-plan items finished.
 *   • auto_advance_usage      — count of times the 3s countdown ran.
 *   • resume_used             — count of times the Continue card was
 *     tapped.
 *   • plan_size_auto_adjusted — count of days the adaptive planSize
 *     changed.
 *   • idle_nudge_shown        — count of 60s idle nudges surfaced.
 *
 * We push to the existing `/api/event` endpoint via `trackEvent`, so
 * no new platform is introduced. Local buffer smooths bursts.
 */
import { trackEvent } from "@/lib/analytics";

const K = {
  cold_open: "mwq_attn_cold_open_ts",
  taps_from_home: "mwq_attn_taps_home",
  last_activity_end: "mwq_attn_last_end_ts",
  active_activity: "mwq_attn_active_id",
  active_activity_start: "mwq_attn_active_start_ts",
};

const _now = () => Date.now();
const _get = (k, d = null) => { try { const v = localStorage.getItem(k); return v == null ? d : v; } catch { return d; } };
const _set = (k, v) => { try { localStorage.setItem(k, String(v)); } catch { /* ignore */ } };
const _del = (k)    => { try { localStorage.removeItem(k); } catch { /* ignore */ } };
const _emit = (name, props = {}) => { try { trackEvent(name, props); } catch { /* ignore */ } };

/** Call once when the app boots. */
export function noteAppOpen() {
  _set(K.cold_open, _now());
  _set(K.taps_from_home, 0);
}

/** Call for every click on a link/button ON a home/plan page. */
export function noteHomeTap() {
  const n = parseInt(_get(K.taps_from_home, "0"), 10) + 1;
  _set(K.taps_from_home, n);
}

/** Call when the child STARTS an activity — records the gap since
 *  last end and the taps-to-first-activity if this is the first. */
export function noteActivityStart(id) {
  _set(K.active_activity, id || "unknown");
  _set(K.active_activity_start, _now());

  // taps_to_first_activity — only emitted for the FIRST activity in a
  // session (after cold open). Then reset until next cold open.
  const cold = parseInt(_get(K.cold_open, "0"), 10);
  const taps = parseInt(_get(K.taps_from_home, "0"), 10);
  if (cold && taps >= 0 && _get("mwq_attn_first_fired") !== "1") {
    _emit("taps_to_first_activity", { count: taps, id });
    _set("mwq_attn_first_fired", "1");
  }

  // gap_between_activities — measured against previous end.
  const lastEnd = parseInt(_get(K.last_activity_end, "0"), 10);
  if (lastEnd) {
    const gap_s = Math.max(0, Math.round((_now() - lastEnd) / 1000));
    _emit("gap_between_activities", { seconds: gap_s, id });
  }
}

/** Call when the child completes / dismisses an activity. */
export function noteActivityEnd(id, { completed = true } = {}) {
  _set(K.last_activity_end, _now());
  const start = parseInt(_get(K.active_activity_start, "0"), 10);
  const active = _get(K.active_activity);
  if (start && !completed) {
    _emit("mid_activity_abandonment", { id: active || id, dwell_s: Math.round((_now() - start) / 1000) });
  }
  _del(K.active_activity);
  _del(K.active_activity_start);
}

export function noteAutoAdvance(id)   { _emit("auto_advance_usage", { id }); }
export function noteResumeUsed(to)    { _emit("resume_used", { to }); }
export function notePlanSizeAdjusted(fromN, toN) { _emit("plan_size_auto_adjusted", { from: fromN, to: toN }); }
export function noteIdleNudge()       { _emit("idle_nudge_shown", {}); }
export function noteSessionComplete(pct) { _emit("session_completion_rate", { pct }); }
